import { businessTimezone } from '#lib/stores/time.js';
import { DateTimefromISO } from '#lib/helper/date.js';
import { page } from '$app/state';
import {
	BookingStatus,
	type Booking,
	type QrCodeScan,
	type QrCodeScanApiResponse
} from '#lib/types/generated/index.js';
import { apiAppQrcodeGetscans } from '#lib/types/generated/api.gen.js';
import { bookingIsConfirmed } from '#lib/stores/booking.js';
import { businessStore } from '#lib/stores/business.js';

import { offerStore } from '#lib/stores/offer.js';
import { productStore } from '#lib/stores/product.js';
import { promoCodeStore } from '#lib/stores/promoCode.js';
import { bookingGetPaymentSummary } from '#lib/helper/payment.js';
import { now } from '#lib/stores/time.js';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';

export type Result = {
	color: string;
	autoHide: boolean;
	sound?: string;
	vibrate?: () => void;
	title?: string;
	description?: string;
};

export type Scan = {
	time: DateTime;
	code: string;
	resultKey: string;
	apiSync: boolean;
	bookingId?: string;
	pax?: number;
};

type ApiScan = {
	id: number;
	time: string;
	code: string;
	bookingId: string;
	pax: number;
};

export const scansHistory = writable<Scan[]>([]);
export let qrCodeBookings = writable<Booking[]>([]);
export let defaultValidSound = writable('valid');
export let fullscreen = writable(false);

export function loadQrCodeData(onlyBookings: boolean = false): Promise<void> {
	return apiAppQrcodeGetscans(page.params.token ?? '', page.params.date ?? '').then((res) => {
		if (!onlyBookings) {
			businessStore.set(res.business);
			businessTimezone.set(res.business.timezone);
			offerStore.set(res.offers);
			productStore.set(res.products);
			promoCodeStore.set(res.promoCodes);
		}

		qrCodeBookings.set(res.bookings);
		if (!onlyBookings) {
			scansHistory.set(
				res.scans.map((scan) => ({
					time: scan.time,
					code: scan.code,
					resultKey: 'SUCCESS',
					apiSync: true,
					bookingId: scan.bookingId,
					pax: scan.pax ?? undefined
				}))
			);
		}
	});
}
export function convertApiScanToScan(scan: ApiScan | QrCodeScan | QrCodeScanApiResponse): Scan {
	return {
		code: scan.code,
		bookingId: scan.bookingId,
		pax: scan.pax ?? undefined,
		time: scan.time instanceof DateTime ? scan.time : DateTimefromISO(scan.time)!,
		resultKey: 'SUCCESS',
		apiSync: true
	};
}
export function loadScansHistoryFromApi(
	scans: (ApiScan | QrCodeScan | QrCodeScanApiResponse)[] | undefined
) {
	if (!scans) {
		scansHistory.set([]);
		return;
	}
	scansHistory.set(scans.map((scan) => convertApiScanToScan(scan)));
}

function addScan(time: DateTime, code: string, apiSync: boolean, resultKey: string = 'SUCCESS') {
	scansHistory.set([
		{
			time: time,
			code: code,
			resultKey: resultKey,
			apiSync: resultKey !== 'SUCCESS'
		},
		...get(scansHistory)
	]);
	localStorage.setItem('qr-code.scans', JSON.stringify(get(scansHistory)));
}

export function scan(test: string): keyof typeof results {
	// uuid+pax
	const bookingId = test.split('+')[0];
	const bookingPax = test.split('+')[1];

	const bookingResult = get(qrCodeBookings).find((b) => b.id === bookingId);
	if (bookingResult) {
		if (!bookingIsConfirmed(bookingResult)) {
			addScan(get(now), test, true, 'CANCELED');
			return 'CANCELED';
		}

		if (+bookingPax <= 0 || +bookingPax > bookingResult.pax) {
			addScan(get(now), test, true, 'NOT_FOUND');
			return 'NOT_FOUND';
		}

		const bookingPayment = bookingGetPaymentSummary(bookingResult);
		if (bookingPayment.remaining > 0 || bookingResult.status === BookingStatus.ToBeConfirmed) {
			addScan(get(now), test, true, 'REQUIRE_ACTION');
			return 'REQUIRE_ACTION';
		}

		if (get(scansHistory).some((scan) => scan.code === test && scan.resultKey === 'SUCCESS')) {
			addScan(get(now), test, true, 'ALREADY_SCANNED');
			return 'ALREADY_SCANNED';
		}

		addScan(get(now), test, false, 'SUCCESS');
		return 'SUCCESS';
	} else {
		addScan(get(now), test, true, 'NOT_FOUND');
		return 'NOT_FOUND';
	}
}

export const results: Record<string, Result> = {
	SUCCESS: {
		color: 'bg-success text-success-on opacity-90',
		autoHide: true,
		vibrate: () => {
			vibrate(100);
			setTimeout(() => vibrate(100), 200);
		}
	},
	ALREADY_SCANNED: {
		color: 'bg-warning text-warning-on',
		autoHide: false,
		sound: 'down',
		vibrate: vibrateError,
		title: 'qr-code.scan.already-scanned.title',
		description: 'qr-code.scan.already-scanned.description'
	},
	NOT_FOUND: {
		color: 'bg-error text-error-on',
		autoHide: false,
		sound: 'down',
		vibrate: vibrateError,
		title: 'qr-code.scan.not-found.title',
		description: 'qr-code.scan.not-found.description'
	},
	CANCELED: {
		color: 'bg-error text-error-on',
		autoHide: false,
		sound: 'down',
		vibrate: vibrateError,
		title: 'qr-code.scan.canceled.title',
		description: 'qr-code.scan.canceled.description'
	},
	REQUIRE_ACTION: {
		color: 'bg-error text-error-on',
		autoHide: false,
		sound: 'cashmachine',
		vibrate: vibrateError,
		title: 'qr-code.scan.require-action.title',
		description: 'qr-code.scan.require-action.description'
	}
};

function vibrateError() {
	vibrate(100);
	setTimeout(() => vibrate(100), 300);
	setTimeout(() => vibrate(400), 500);
}

function vibrate(duration: number): void {
	if ('vibrate' in navigator) {
		navigator.vibrate(duration); // 1000 ms = 1 second
	}
}
